import { apiClient } from '../utils/apiClient';
import type {
    SubscriptionTier,
    BillingInfo,
    SubscribeToTierRequest
} from '@arrowise/shared';

/**
 * Subscription Plan Interface
 */
export interface SubscriptionPlan {
    tier: SubscriptionTier;
    name: string;
    price: number;
    currency: string;
    interval: string | null;
    features: string[];
}

/**
 * Current Subscription Response
 */
export interface CurrentSubscriptionResponse {
    subscription: {
        tier: SubscriptionTier;
        status: string;
        currentPeriodEnd: string | null;
        stripeCustomerId: string | null;
        stripeSubscriptionId: string | null;
    };
    billingInfo: BillingInfo | null;
}

/**
 * Billing History Item
 */
export interface BillingHistoryItem {
    id: string;
    date: string;
    amount: number;
    currency: string;
    status: string;
    description: string;
    downloadUrl: string | null;
}

/**
 * Billing History Response
 */
export interface BillingHistoryResponse {
    invoices: BillingHistoryItem[];
}

/**
 * Subscribe Response
 */
export interface SubscribeResponse {
    user: any;
    team?: any;
    teamsRemoved?: string[];
    membershipsArchived?: number;
}

/**
 * Cancel Subscription Response
 */
export interface CancelSubscriptionResponse {
    user: any;
    teamsRemoved?: string[];
    membershipsArchived?: number;
}

export interface CheckoutSessionResponse {
    url: string;
}

export interface PortalSessionResponse {
    url: string;
}

/**
 * Billing Service Class
 */
class BillingService {

    /**
     * Get current subscription
     */
    async getCurrentSubscription(): Promise<CurrentSubscriptionResponse> {
        const response = await apiClient.get<CurrentSubscriptionResponse>('/billing/subscription');
        if (!response.data) {
            throw new Error('Failed to fetch current subscription');
        }
        return response.data;
    }

    /**
     * Subscribe to a tier
     */
    async subscribeToTier(subscriptionData: SubscribeToTierRequest): Promise<SubscribeResponse> {
        const response = await apiClient.post<SubscribeResponse>('/billing/subscribe', subscriptionData);
        if (!response.data) {
            throw new Error('Failed to subscribe to tier');
        }
        return response.data;
    }

    /**
     * Cancel subscription (downgrade to free)
     */
    async cancelSubscription(): Promise<CancelSubscriptionResponse> {
        const response = await apiClient.post<CancelSubscriptionResponse>('/billing/cancel');
        if (!response.data) {
            throw new Error('Failed to cancel subscription');
        }
        return response.data;
    }

    /**
     * Create Stripe Checkout session and return redirect URL
     */
    async createCheckoutSession(subscriptionData: SubscribeToTierRequest): Promise<CheckoutSessionResponse> {
        const response = await apiClient.post<CheckoutSessionResponse>('/billing/checkout', subscriptionData);
        if (!response.data) {
            throw new Error('Failed to create checkout session');
        }
        return response.data;
    }

    /**
     * Create a Customer Portal session and return redirect URL
     */
    async getPortalUrl(): Promise<PortalSessionResponse> {
        const response = await apiClient.get<PortalSessionResponse>('/billing/portal');
        if (!response.data) {
            throw new Error('Failed to create portal session');
        }
        return response.data;
    }

    /**
     * Get billing history
     */
    async getBillingHistory(): Promise<BillingHistoryResponse> {
        const response = await apiClient.get<BillingHistoryResponse>('/billing/history');
        if (!response.data) {
            throw new Error('Failed to fetch billing history');
        }
        return response.data;
    }

    /**
     * Test tier access
     */
    async testTierAccess(tier: 'starter' | 'team'): Promise<{ success: boolean; message: string }> {
        const response = await apiClient.get<{ success: boolean; message: string }>(`/billing/test/${tier}`);
        if (!response.data) {
            throw new Error('Failed to test tier access');
        }
        return response.data;
    }
}

// Create and export singleton instance
export const billingService = new BillingService();
export default billingService;
